import { useState, useCallback } from "react";
import { publishPostService, getPublishingStatusService } from "../services/publishingService";

export interface PublishStatus {
  postId: string;
  overallStatus: "not_published" | "pending" | "publishing" | "published" | "partially_published" | "failed";
  platforms: {
    [key: string]: {
      id: string;
      platform: string;
      status: string;
      platformPostId?: string;
      platformPermalink?: string;
      errorCode?: string;
      errorMessage?: string;
      retryCount?: number;
      publishedAt?: string;
      createdAt?: string;
    };
  };
}

export interface PublishError {
  message: string;
  code?: string;
  platform?: string;
  platformPostId?: string;
  platformPermalink?: string;
}

export const usePublishPost = () => {
  const [isPublishing, setIsPublishing] = useState(false);
  const [publishError, setPublishError] = useState<string | null>(null);
  const [publishingStatus, setPublishingStatus] = useState<PublishStatus | null>(null);

  const publishPost = useCallback(
    async (postId: string, clientId: string, platforms?: string[], force?: boolean) => {
      setIsPublishing(true);
      setPublishError(null);

      try {
        const result = await publishPostService(postId, clientId, platforms, force);

        if (!result.success) {
          setPublishError(result.errors?.join(", ") || "Failed to publish post");
          return { success: false, error: { message: result.errors?.join(", ") || "Failed to publish post" } as PublishError };
        }

        return { success: true, data: result };
      } catch (error: any) {
        const publishErr: PublishError = {
          message: error.message || "Failed to publish post",
          code: error.code,
          platform: error.platform,
          platformPostId: error.platformPostId,
          platformPermalink: error.platformPermalink,
        };

        // Only set generic error message if not ALREADY_PUBLISHED (let caller handle)
        if (error.code !== "ALREADY_PUBLISHED") {
          setPublishError(publishErr.message);
        }

        return { success: false, error: publishErr };
      } finally {
        setIsPublishing(false);
      }
    },
    []
  );

  const getPublishingStatus = useCallback(async (postId: string) => {
    try {
      const status = await getPublishingStatusService(postId);
      setPublishingStatus(status);
      return status;
    } catch (error: any) {
      console.error("Failed to get publishing status:", error);
      return null;
    }
  }, []);

  const clearError = useCallback(() => {
    setPublishError(null);
  }, []);

  return {
    publishPost,
    getPublishingStatus,
    isPublishing,
    publishError,
    publishingStatus,
    clearError,
  };
};
